import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  AdminCustomDomainInfo,
  AuditEventInfo,
  PaginatedAdminCustomDomains,
  PaginatedAuditEvents,
  ProjectCustomDomainStatus,
} from '@claude-code-api/shared';
import {
  Activity,
  Ban,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Globe2,
  History,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@/components/ui';

interface ApiResponse<T> {
  data?: T;
  error?: string;
}

interface PaginationState {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type GuardedAction = 'suspend' | 'remove';
type StatusFilter = ProjectCustomDomainStatus | 'all';

const INITIAL_PAGINATION: PaginationState = { page: 1, limit: 25, total: 0, totalPages: 0 };

export default function AdminCustomDomainsPage() {
  const { t } = useTranslation(['admin', 'common']);
  const [domains, setDomains] = useState<AdminCustomDomainInfo[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [guarded, setGuarded] = useState<{
    domain: AdminCustomDomainInfo;
    action: GuardedAction;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [auditDomain, setAuditDomain] = useState<AdminCustomDomainInfo | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [events, setEvents] = useState<AuditEventInfo[]>([]);

  const loadDomains = useCallback(async () => {
    setLoading(true);
    const query = new URLSearchParams({
      search,
      page: String(pagination.page),
      limit: String(pagination.limit),
    });
    if (status !== 'all') query.set('status', status);
    try {
      const response = await fetch(`/api/admin/custom-domains?${query.toString()}`, {
        credentials: 'include',
      });
      const result = await response.json() as ApiResponse<PaginatedAdminCustomDomains>;
      if (!response.ok || !result.data) {
        throw new Error(result.error ?? t('admin:customDomains.errors.load'));
      }
      setDomains(result.data.domains);
      setPagination(result.data.pagination);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('admin:customDomains.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page, search, status, t]);

  useEffect(() => {
    const timeout = window.setTimeout(() => void loadDomains(), 250);
    return () => window.clearTimeout(timeout);
  }, [loadDomains]);

  const requestAction = async (
    domain: AdminCustomDomainInfo,
    action: 'recheck-dns' | 'reconcile',
  ): Promise<void> => {
    setBusyId(domain.id);
    try {
      const response = await fetch(`/api/admin/custom-domains/${domain.id}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      const result = await response.json() as ApiResponse<never>;
      if (!response.ok) throw new Error(result.error ?? t('admin:customDomains.errors.action'));
      toast.success(t(action === 'recheck-dns'
        ? 'admin:customDomains.recheckComplete'
        : 'admin:customDomains.reconcileQueued'));
      await loadDomains();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('admin:customDomains.errors.action'));
    } finally {
      setBusyId(null);
    }
  };

  const confirmGuardedAction = async (): Promise<void> => {
    if (!guarded || !reason.trim()) return;
    setBusyId(guarded.domain.id);
    try {
      const endpoint = `/api/admin/custom-domains/${guarded.domain.id}${
        guarded.action === 'suspend' ? '/suspend' : ''
      }`;
      const response = await fetch(endpoint, {
        method: guarded.action === 'suspend' ? 'POST' : 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      const result = await response.json() as ApiResponse<never>;
      if (!response.ok) throw new Error(result.error ?? t('admin:customDomains.errors.action'));
      toast.success(t(`admin:customDomains.${guarded.action}Accepted`));
      setGuarded(null);
      setReason('');
      await loadDomains();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('admin:customDomains.errors.action'));
    } finally {
      setBusyId(null);
    }
  };

  const openAudit = async (domain: AdminCustomDomainInfo): Promise<void> => {
    setAuditDomain(domain);
    setAuditLoading(true);
    setEvents([]);
    try {
      const query = new URLSearchParams({
        resourceType: 'custom_domain',
        resourceId: domain.id,
        limit: '100',
      });
      const response = await fetch(`/api/admin/audit-events?${query.toString()}`, {
        credentials: 'include',
      });
      const result = await response.json() as ApiResponse<PaginatedAuditEvents>;
      if (!response.ok || !result.data) {
        throw new Error(result.error ?? t('admin:customDomains.errors.audit'));
      }
      setEvents(result.data.events);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('admin:customDomains.errors.audit'));
    } finally {
      setAuditLoading(false);
    }
  };

  const changeSearch = (value: string): void => {
    setSearch(value);
    setPagination((current) => ({ ...current, page: 1 }));
  };

  const changeStatus = (value: StatusFilter): void => {
    setStatus(value);
    setPagination((current) => ({ ...current, page: 1 }));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin:customDomains.title')}
        description={t('admin:customDomains.description')}
      />

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative min-w-0 flex-1 sm:max-w-xl">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            value={search}
            maxLength={200}
            onChange={(event) => changeSearch(event.target.value)}
            placeholder={t('admin:customDomains.search')}
            aria-label={t('admin:customDomains.search')}
          />
        </div>
        <Select value={status} onValueChange={(value) => changeStatus(value as StatusFilter)}>
          <SelectTrigger className="w-full sm:w-48" aria-label={t('admin:customDomains.statusFilter')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(['all', 'pending', 'active', 'degraded', 'suspended', 'deleting'] as const)
              .map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`admin:customDomains.statuses.${value}`)}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid gap-3">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      ) : null}
      {!loading && domains.length === 0 ? (
        <Card className="shadow-none">
          <CardContent className="py-12 text-center text-muted-foreground">
            {t('admin:customDomains.empty')}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4">
        {domains.map((domain) => (
          <Card key={domain.id} className="shadow-none">
            <CardContent className="grid gap-4 p-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Globe2 className="h-4 w-4 shrink-0 text-primary" />
                  <strong className="break-all">{domain.hostname}</strong>
                  <Badge variant={domain.status === 'active'
                    ? 'success'
                    : domain.status === 'suspended'
                      ? 'destructive'
                      : 'secondary'}>
                    {t(`admin:customDomains.statuses.${domain.status}`)}
                  </Badge>
                  <Badge variant="outline">
                    {t('admin:customDomains.tls', { status: domain.certificateStatus })}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <Link className="break-all hover:text-primary hover:underline" to={`/projects/${domain.project.id}`}>
                    {domain.project.name}
                  </Link>
                  <span className="break-all">{domain.owner.email}</span>
                  {domain.session ? (
                    <Link className="break-all hover:text-primary hover:underline" to={`/sessions/${domain.session.id}`}>
                      {domain.session.id} · {domain.session.status}
                    </Link>
                  ) : <span>{t('admin:customDomains.noSession')}</span>}
                </div>
                <p className="break-words text-xs text-muted-foreground">
                  {domain.reconciliationError ?? domain.lastError ?? domain.transitionReason}
                </p>
              </div>
              <div className="flex items-center lg:justify-end">
                <ButtonGroup>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shadow-none hover:shadow-none"
                    disabled={busyId === domain.id || domain.status === 'deleting'}
                    onClick={() => void requestAction(domain, 'recheck-dns')}
                  >
                    <RefreshCw className="mr-1 h-4 w-4" />
                    {t('admin:customDomains.actions.recheck')}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-8 px-0 shadow-none hover:shadow-none"
                        aria-label={t('admin:customDomains.actions.more')}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem
                        disabled={busyId === domain.id}
                        onSelect={() => void requestAction(domain, 'reconcile')}
                      >
                        <Activity className="h-4 w-4" />
                        {t('admin:customDomains.actions.reconcile')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => void openAudit(domain)}>
                        <History className="h-4 w-4" />
                        {t('admin:customDomains.actions.audit')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        disabled={!['active', 'degraded'].includes(domain.status)}
                        onSelect={() => setGuarded({ domain, action: 'suspend' })}
                      >
                        <Ban className="h-4 w-4" />
                        {t('admin:customDomains.actions.suspend')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                        disabled={domain.status === 'deleting'}
                        onSelect={() => setGuarded({ domain, action: 'remove' })}
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('admin:customDomains.actions.remove')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </ButtonGroup>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {pagination.totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3">
          <Button
            size="sm"
            variant="outline"
            disabled={pagination.page <= 1 || loading}
            onClick={() => setPagination((current) => ({ ...current, page: current.page - 1 }))}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            {t('admin:customDomains.previous')}
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('admin:customDomains.pagination', {
              page: pagination.page,
              totalPages: pagination.totalPages,
              total: pagination.total,
            })}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={pagination.page >= pagination.totalPages || loading}
            onClick={() => setPagination((current) => ({ ...current, page: current.page + 1 }))}
          >
            {t('admin:customDomains.next')}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        open={guarded !== null}
        onOpenChange={(open) => {
          if (!open) {
            setGuarded(null);
            setReason('');
          }
        }}
        title={t(`admin:customDomains.confirm.${guarded?.action ?? 'suspend'}.title`)}
        description={t(`admin:customDomains.confirm.${guarded?.action ?? 'suspend'}.description`)}
        confirmLabel={t(`admin:customDomains.actions.${guarded?.action ?? 'suspend'}`)}
        confirmDisabled={!reason.trim()}
        loading={busyId !== null}
        variant="destructive"
        onConfirm={() => void confirmGuardedAction()}
      >
        <div className="space-y-2">
          <Label htmlFor="domain-action-reason">{t('admin:customDomains.reason')}</Label>
          <Input
            id="domain-action-reason"
            value={reason}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </ConfirmDialog>

      <Dialog
        open={auditDomain !== null}
        onOpenChange={(open) => {
          if (!open) setAuditDomain(null);
        }}
      >
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin:customDomains.auditTitle')}</DialogTitle>
            <DialogDescription className="break-all">{auditDomain?.hostname}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {auditLoading ? <Skeleton className="h-24" /> : null}
            {!auditLoading && events.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('admin:customDomains.noAudit')}</p>
            ) : null}
            {events.map((event) => (
              <div key={event.id} className="rounded-lg border p-3 text-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <strong className="break-all">{event.action}</strong>
                  <Badge variant="outline">{event.outcome}</Badge>
                  <time className="text-xs text-muted-foreground">
                    {new Date(event.createdAt).toLocaleString()}
                  </time>
                </div>
                <p className="mt-1 break-words text-muted-foreground">{event.reason}</p>
                <p className="mt-1 break-all text-xs">{event.actorEmail ?? event.actorType}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
